<template>
  <div class="article-list-page">
    <NavBar />
    
    <div class="page-container">
      <!-- 页面标题 -->
      <div class="page-header">
        <span class="back-link" @click="$router.push('/')">← 返回首页</span>
        <h1 class="page-title">{{ categoryName || '文章资讯' }}</h1>
      </div>
      
      <!-- 文章列表 -->
      <div class="article-list" v-if="articles.length > 0">
        <div 
          class="article-item" 
          v-for="article in articles" 
          :key="article.id"
          @click="$router.push(`/article/${article.id}`)"
        >
          <div class="item-thumb">
            <img :src="article.cover_url" :alt="article.title" />
          </div>
          <div class="item-content">
            <h3 class="item-title">{{ article.title }}</h3>
            <div class="item-meta">
              <span>{{ article.contact_name || '匿名' }}</span>
              <span class="dot"></span>
              <span>{{ formatTime(article.createtime) }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 空状态 -->
      <div class="empty-state" v-else>
        <p>暂无文章</p>
      </div>
      
      <!-- 分页 -->
      <div class="pagination" v-if="total > limit">
        <span 
          class="page-btn" 
          :class="{ disabled: page <= 1 }"
          @click="page > 1 && handlePageChange(page - 1)"
        >上一页</span>
        <span class="page-info">{{ page }} / {{ Math.ceil(total / limit) }}</span>
        <span 
          class="page-btn" 
          :class="{ disabled: page >= Math.ceil(total / limit) }"
          @click="page < Math.ceil(total / limit) && handlePageChange(page + 1)"
        >下一页</span>
      </div>
    </div>
    
    <Footer />
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import Footer from '@/components/Footer.vue'
import { getArticleList, getArticleCategories } from '@/api/index.js'

export default {
  name: 'ArticleList',
  components: {
    NavBar,
    Footer
  },
  data() {
    return {
      articles: [],
      categoryId: 0,
      categoryName: '',
      page: 1,
      limit: 10,
      total: 0
    }
  },
  watch: {
    '$route.params.categoryId': {
      handler() {
        this.loadArticles()
      },
      immediate: true
    }
  },
  methods: {
    async loadArticles() {
      this.categoryId = parseInt(this.$route.params.categoryId) || 0
      
      // 获取分类名称
      if (this.categoryId > 0) {
        try {
          const result = await getArticleCategories()
          if (result.code === 1 && result.data) {
            const category = result.data.find(cat => cat.id === this.categoryId)
            this.categoryName = category ? category.name : '文章资讯'
          }
        } catch (error) {
          console.error('获取分类失败:', error)
        }
      }
      
      // 获取文章列表
      try {
        const params = {
          page: this.page,
          limit: this.limit
        }
        
        if (this.categoryId > 0) {
          params.category_id = this.categoryId
        }
        
        const result = await getArticleList(params)
        if (result.code === 1 && result.data) {
          this.articles = result.data.list || []
          this.total = result.data.total || 0
        }
      } catch (error) {
        console.error('加载文章列表失败:', error)
        this.$message.error('加载失败，请稍后重试')
      }
    },
    handlePageChange(page) {
      this.page = page
      this.loadArticles()
      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    formatTime(timestamp) {
      const date = new Date(timestamp * 1000)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
  }
}
</script>

<style scoped>
.article-list-page {
  min-height: 100vh;
  background: #fff;
}

.page-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

.page-header {
  margin-bottom: 40px;
}

.back-link {
  font-size: 14px;
  color: #666;
  cursor: pointer;
  display: inline-block;
  margin-bottom: 20px;
  transition: color 0.2s;
}

.back-link:hover {
  color: #111;
}

.page-title {
  font-size: 32px;
  font-weight: 800;
  color: #111;
  margin: 0;
  padding-bottom: 16px;
  border-bottom: 2px solid #111;
}

.article-list {
  display: flex;
  flex-direction: column;
}

.article-item {
  display: flex;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s;
}

.article-item:hover {
  background: #fafafa;
  margin: 0 -16px;
  padding: 20px 16px;
}

.item-thumb {
  width: 140px;
  height: 90px;
  flex-shrink: 0;
  overflow: hidden;
}

.item-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.item-title {
  font-size: 17px;
  font-weight: 600;
  color: #111;
  line-height: 1.5;
  margin: 0 0 10px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.article-item:hover .item-title {
  text-decoration: underline;
}

.item-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #999;
}

.item-meta .dot {
  width: 3px;
  height: 3px;
  background: #ccc;
  border-radius: 50%;
}

.empty-state {
  text-align: center;
  padding: 80px 0;
  color: #999;
  font-size: 15px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 24px;
  margin-top: 50px;
  padding-top: 24px;
  border-top: 1px solid #eee;
}

.page-btn {
  font-size: 14px;
  font-weight: 500;
  color: #111;
  cursor: pointer;
  padding: 8px 16px;
  border: 1px solid #ddd;
  transition: all 0.2s;
}

.page-btn:hover:not(.disabled) {
  background: #111;
  color: #fff;
  border-color: #111;
}

.page-btn.disabled {
  color: #ccc;
  border-color: #eee;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

@media (max-width: 768px) {
  .page-container {
    padding: 24px 16px 60px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .article-item {
    flex-direction: column;
    gap: 12px;
  }
  
  .item-thumb {
    width: 100%;
    height: 180px;
  }
  
  .item-title {
    font-size: 16px;
  }
}
</style>
